import React, { FunctionComponent, useState } from 'react';
import { useHistory } from 'react-router-dom';
import apiSvg from '../../../assets/images/integrations/api.svg';
import rapidapiLogo from '../../../assets/images/integrations/rapidapi.svg';
import slackLogo from '../../../assets/images/integrations/slack-mark-web.png';
import zapierLogo from '../../../assets/images/integrations/zapier.svg';
import ConnectReportToSlackChannelDialog from '../../../components/connect-report-to-slack-channel-dialog/ConnectReportToSlackChannelDialog';
import { useAccount } from '../../../hooks/useAccount';
import { useBriefsOverview } from '../../../hooks/useBriefs';
import { useAvailableIntegration } from '../../../hooks/useAvailableIntegration';
import { useFeatureFlag } from '../../../hooks/useFeatureFlag';
import { useSubscription } from '../../../hooks/useSubscription';
import SettingsPageLayout from '../../../layouts/settings/settingsPageLayout';
import { ApiApp } from '../../../services/models/api-app';
import { AppType } from '../../../services/models/app-type';
import { Brief } from '../../../services/models/brief';
import { FeatureFlag } from '../../../services/models/feature-flags';
import { createMailtoLink } from '../../../utils/createMailtoLinks';
import { BillingRoute } from '../../../utils/Routes';

const IntegrationsPage: FunctionComponent = () => {
  const history = useHistory();

  const [showSlackDialog, setShowSlackDialog] = useState(false);

  const subscription = useSubscription();
  const { briefs: briefItems } = useBriefsOverview();
  const briefs: Brief[] = briefItems || [];
  const { account } = useAccount();
  const apiApps: ApiApp[] = account?.apiApps || [];
  const slackFeatureFlag = useFeatureFlag(FeatureFlag.ShowAddToSlack);
  const zapierIntegration = useAvailableIntegration(AppType.Zapier);
  const slackIntegration = useAvailableIntegration(AppType.Slack);

  const appExists = (appType: AppType) => {
    return apiApps.some(x => x.type === appType);
  };

  return (
    <SettingsPageLayout title="Integrations | Newslit">
      <div className="mt-10 divide-y divide-gray-200">
        <div className="space-y-1">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Get the News wherever you need them!
          </h3>
          <p className="max-w-2xl text-sm text-gray-500 m-0">
            Share your News Briefs with your team, users, clients by using our integration options.
          </p>
        </div>

        <div className="mt-6">
          <div className="flex flex-col divide-y divide-gray-200">
            <IntegrationItem
              logo={zapierLogo}
              title="Zapier"
              description="Automatically sync your News Briefs with other services using Zapier."
              buttonLabel={zapierIntegration ? 'Connect' : 'Upgrade Plan'}
              onButtonClick={() => {
                history.push(
                  zapierIntegration ? '/dashboard/settings/integrations/zapier' : BillingRoute
                );
              }}
            />

            {slackFeatureFlag && (
              <>
                <IntegrationItem
                  logo={slackLogo}
                  title="Slack"
                  description="Receive your News Briefs via any Slack channel."
                  buttonLabel={
                    slackIntegration && !!subscription.active ? 'Connect' : 'Upgrade Plan'
                  }
                  onButtonClick={() => {
                    if (slackIntegration && !!subscription.active) {
                      setShowSlackDialog(true);
                    } else {
                      history.push(BillingRoute);
                    }
                  }}
                />

                {briefs && showSlackDialog && (
                  <ConnectReportToSlackChannelDialog
                    briefs={briefs}
                    open={showSlackDialog}
                    onClose={() => setShowSlackDialog(false)}
                  />
                )}
              </>
            )}

            {appExists(AppType.Api) && (
              <IntegrationItem
                logo={apiSvg}
                title="API"
                description="Integrate your briefs into your application or website through a single API."
                buttonLabel="Learn More"
                onButtonClick={() => {
                  history.push('/dashboard/settings/integrations/api');
                }}
              />
            )}

            {appExists(AppType.RapidApi) && (
              <IntegrationItem
                logo={rapidapiLogo}
                title="RapidAPI"
                description="Integrate your reports into your application or website through a single API."
                buttonLabel="Learn More"
                onButtonClick={() => {
                  history.push('/dashboard/settings/integrations/rapidapi');
                }}
              />
            )}
          </div>
        </div>

        <div className="mt-10 pt-8 border-t border-gray-200">
          <p className="text-base font-medium text-gray-900">
            Can't find the integration you're looking for?{' '}
            <a
              href={createMailtoLink(
                'help@newslit.co',
                'Integration Request',
                'What new integrations would you like to see added to our platform?'
              )}
              className="font-semibold text-secondary hover:text-secondary-dark underline decoration-1 underline-offset-2"
            >
              Let us know
            </a>
            .
          </p>
        </div>
      </div>
    </SettingsPageLayout>
  );
};

export default IntegrationsPage;

interface IntegrationItemProps {
  logo: string;
  title: string;
  description: string;
  buttonLabel: string;
  onButtonClick: () => void;
}

function IntegrationItem(props: IntegrationItemProps) {
  return (
    <div className="flex justify-between items-center p-3">
      <div className="w-16 shrink-0">
        <img className="h-10 p-1 object-contain" src={props.logo} alt={`${props.title} Logo`} />
      </div>

      <div className="grow">
        <div className="text-gray-900 font-light text-lg">{props.title}</div>
        <div className="text-gray-400 font-light text-base">{props.description}</div>
      </div>

      <div className="ml-4 shrink-0 justify-center items-end">
        <button
          type="button"
          className="mt-3 w-full inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-xs font-medium rounded-md text-white bg-secondary hover:bg-secondary-dark focus:outline-hidden focus:ring-2 focus:ring-offset-2 focus:ring-secondary sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
          onClick={props.onButtonClick}
        >
          {props.buttonLabel}
        </button>
      </div>
    </div>
  );
}
